import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum, IFile } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import UploadFileInput from '@repo/ui/file-input'
import { DeleteIcon, ImageIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { toast } from 'sonner'

import TextInput from '../../../../../packages/ui/components/atoms/input/text-input'
import useTranslate from '../../lib/hooks/use-translate'

type Props = {
  index: number
  image: Partial<IFile>
  onTextChange: (text: string) => void
  onImageUpload: (file: IFile[]) => void
  onImageDelete: () => void
  onCardDelete: () => void
}

const UploadImageCard = ({
  image,
  index,
  onTextChange,
  onImageUpload,
  onCardDelete,
  onImageDelete
}: Props) => {
  const { t } = useTranslate()

  return (
    <InnerBox className="p-4 rounded-xl flex flex-col gap-2 mobile:p-3">
      <TextInput
        type="text"
        autoComplete={`caption-${index}`} // Unique to avoid autofill
        label={t('image.card.caption.label')}
        value={image.caption}
        onChange={(e) => onTextChange(e.target.value)}
      />
      <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
        {t('image.card.upload.label')}
      </Typography>
      {image.path ? (
        <InnerBox className="bg-white-hover rounded-lg p-2">
          <img src={image.path} alt="Image" className="w-full h-40 object-cover rounded-lg" />
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <Typography
                variant="tag"
                color={tailwindColors.grey.DEFAULT}
                className="truncate max-w-[120px] overflow-hidden"
              >
                {image.name || 'Image name'}
              </Typography>
              <Typography variant="label" color={tailwindColors.grey.light.active}>
                {((image?.size ?? 0) / 1024).toFixed(0)}KB
              </Typography>
            </div>
            <ActionButton icon={DeleteIcon} variant="danger" onClick={onImageDelete} />
          </div>
        </InnerBox>
      ) : (
        <>
          <UploadFileInput
            className="w-full"
            icon={ImageIcon}
            accept="image/*"
            multiple={false}
            maxSize={2 * 1024 * 1024} // 2MB
            types={[FileTypeEnum.IMAGE]}
            placeholder={t('image.card.drag.label')}
            onFilesChange={onImageUpload}
            onFileError={
              (error) => toast.error(error) // or use your translation key
            }
          />
          <ActionButton
            className="w-full"
            icon={DeleteIcon}
            variant="danger"
            onClick={onCardDelete}
          />
        </>
      )}
    </InnerBox>
  )
}

export default UploadImageCard
